import { useMemo } from 'react';

import type { CrmObject } from '@/types/crm';

import { useCrmObjectLive, useCrmObjectSnapshot } from './useCrmObjectSnapshot';

/**
 * The two sides of a proposal diff for one CRM record, read together.
 */
export interface CrmObjectDiff {
  /**
   * The record as it is RIGHT NOW — no proposal projected onto it. This is
   * the LEFT / "current" column of a diff.
   */
  current: CrmObject | undefined;
  /**
   * The record as the active proposal would leave it — the live record with
   * any active preview projection merged on top. This is the RIGHT /
   * "proposed" column.
   */
  projected: CrmObject | undefined;
}

/**
 * useCrmObjectDiff — the sanctioned read for any surface rendering
 * "current vs proposed" for a CRM record.
 *
 * Reach for this INSTEAD of choosing between `useCrmObjectLive` and
 * `useCrmObjectSnapshot` yourself. Inside a `CrmPreviewProvider` (mounted by
 * every proposal detail surface) the projected view has the proposal already
 * merged in, so sourcing a diff's current column from it makes both columns
 * show the proposed value — a silent no-op diff. That exact bug has shipped
 * before (the stage-transition card); this hook exists so a diff author never
 * has to make the choice at all. See
 * `docs/claude/FRONTEND_PATTERNS.md` § "Live vs. projected".
 *
 * Also NOT the fix: deriving the current side from a value copied onto the
 * action (`original_*` fields, a backend `current_values` blob). A copy taken
 * at proposal time answers "what was true when this was written" and drifts
 * the moment the record changes; `current` here stays live via the store's
 * WebSocket updates.
 *
 * Cost: both views share one store subscription and one overlay subscription
 * under the hood (`useCrmObjectView` computes both and returns one), so
 * reading the pair costs no extra fetch over reading either side alone.
 *
 * Outside any active preview, `current` and `projected` are the same store
 * object — rendering a diff from this hook degrades gracefully to
 * "no changes".
 *
 * @param entityId - The entity UUID to subscribe to. If undefined, both sides are undefined.
 * @param orgId - Organization ID for triggering fetch. If undefined or <= 0, skips fetch.
 *
 * @example
 * const { current, projected } = useCrmObjectDiff(dealId, orgId);
 * const currentStageId = findStage(current?.associations);   // left column
 * const proposedStageId = findStage(projected?.associations); // right column
 */
export function useCrmObjectDiff(
  entityId: string | undefined,
  orgId: number | undefined
): CrmObjectDiff {
  const current = useCrmObjectLive(entityId, orgId);
  const projected = useCrmObjectSnapshot(entityId, orgId);
  return useMemo(() => ({ current, projected }), [current, projected]);
}
